import type { JSX } from 'react';

import type { FolderTile, Tile as TileSchema } from '@schema/tiles.js';

import { Tile } from '../../Tile.js';
import { LauncherModal } from './LauncherModal.js';

export interface FolderModalProps {
  folder: FolderTile;
  /**
   * Dispatch handler — same one the main grid uses. Child taps go
   * through engine.dispatchTile so the existing failure UX (demo
   * toasts, package-not-installed handling) applies unchanged.
   */
  onTileActivate: (tile: TileSchema) => Promise<void> | void;
  onClose: () => void;
  /** When provided, the modal footer shows a "← Back" affordance. */
  onBack?: () => void;
}

/**
 * Folder-content modal. Opened when the user taps a folder tile in
 * the launcher grid; renders the folder's children as a tight 3-
 * column grid using the same Tile component as the main launcher.
 *
 * Closes when the user taps a child (after the dispatch fires) so
 * the kiosk's main view comes back into focus without a manual
 * dismiss. Wrong-package errors etc. surface via the engine's
 * normal toast path — the modal itself doesn't render an error UI.
 */
export function FolderModal({
  folder,
  onTileActivate,
  onClose,
  onBack,
}: FolderModalProps): JSX.Element {
  async function handleChildActivate(tile: TileSchema): Promise<void> {
    // Fire-and-forget — close on success or failure either way; the
    // toast surfaces the failure if any.
    try {
      await onTileActivate(tile);
    } finally {
      onClose();
    }
  }

  return (
    <LauncherModal
      title={folder.label}
      onClose={onClose}
      testId="folder-modal"
      {...(onBack && { onBack })}
    >
      <div className="folder-modal__grid" data-testid="folder-modal-grid">
        {folder.children.map((child) => (
          <Tile
            key={child.id}
            tile={child}
            onActivate={(t) => {
              void handleChildActivate(t);
            }}
          />
        ))}
      </div>
    </LauncherModal>
  );
}
